import { Descriptions, Tag } from 'antd';
import { Link } from 'react-router';
import { formatMoney } from '../../../../../shared/policies/format-money';
import { truckTabPath } from '../../../../fleet/features/trucks/trucks.paths';
import { useTruckRegistrations } from '../hooks/use-truck-registrations';
import {
  WORK_ORDER_SEVERITY_COLORS,
  WORK_ORDER_SEVERITY_LABELS,
  WORK_ORDER_SOURCE_LABELS,
  WORK_ORDER_STATE_COLORS,
  WORK_ORDER_STATE_LABELS,
} from '../policies/work-order-labels';
import type { WorkOrderView } from '../work-orders.api';

function when(instant: string | null): string {
  return instant ? new Date(instant).toLocaleString('en-KE') : '—';
}

/** What the order is and where it has got to. A rejection stays visible on a raised order until it is approved or cancelled, so the raiser can read why. */
export function WorkOrderSummary({ order }: { order: WorkOrderView }) {
  const registration = useTruckRegistrations([order.truckId]).get(order.truckId);

  return (
    <Descriptions column={{ xs: 1, md: 2 }} size="small" bordered>
      <Descriptions.Item label="State">
        <Tag color={WORK_ORDER_STATE_COLORS[order.state]}>{WORK_ORDER_STATE_LABELS[order.state]}</Tag>
      </Descriptions.Item>
      <Descriptions.Item label="Severity">
        <Tag color={WORK_ORDER_SEVERITY_COLORS[order.severity]}>{WORK_ORDER_SEVERITY_LABELS[order.severity]}</Tag>
      </Descriptions.Item>
      <Descriptions.Item label="Truck">
        <Link to={truckTabPath(order.truckId, 'details')}>{registration ?? 'Truck'}</Link>
      </Descriptions.Item>
      <Descriptions.Item label="Source">
        {WORK_ORDER_SOURCE_LABELS[order.source]}
        {order.sourceRef ? ` · ${order.sourceRef}` : ''}
      </Descriptions.Item>
      <Descriptions.Item label="Estimate">{formatMoney(order.estimatedCost)}</Descriptions.Item>
      <Descriptions.Item label="Raised">{when(order.raisedAt)}</Descriptions.Item>
      <Descriptions.Item label="Approved">{when(order.approvedAt)}</Descriptions.Item>
      <Descriptions.Item label="Scheduled for">{order.scheduledFor ?? '—'}</Descriptions.Item>
      <Descriptions.Item label="Started">{when(order.startedAt)}</Descriptions.Item>
      <Descriptions.Item label="Completed">{when(order.completedAt)}</Descriptions.Item>
      <Descriptions.Item label="Verified">{when(order.verifiedAt)}</Descriptions.Item>
      {order.description && (
        <Descriptions.Item label="Details" span="filled">
          {order.description}
        </Descriptions.Item>
      )}
      {order.rejectionReason && (
        <Descriptions.Item label="Rejected because" span="filled">
          {order.rejectionReason}
        </Descriptions.Item>
      )}
      {order.cancelReason && (
        <Descriptions.Item label="Cancelled because" span="filled">
          {order.cancelReason}
        </Descriptions.Item>
      )}
    </Descriptions>
  );
}
